import { useLenis } from "lenis/react";
import { useNavigate } from "react-router-dom";
import { ArrowUp, Instagram, Linkedin, Facebook, Lock } from "lucide-react";
import { LogoMark } from "./Logo";
import { Reveal } from "./Reveal";

const NAV = [
  { label: "Servizi", id: "#servizi" },
  { label: "Progetti", id: "#casi-studio" },
  { label: "Il Metodo", id: "#metodo" },
  { label: "Piani", id: "#piani" },
  { label: "FAQ", id: "#faq" },
  { label: "Contatti", id: "#contatti" },
  { label: "Demo interattive", id: "/demo" },
];

export default function Footer() {
  const lenis = useLenis();
  const navigate = useNavigate();

  const go = (id) => (e) => {
    e.preventDefault();
    if (id.startsWith("/")) {
      navigate(id);
      return;
    }
    if (window.location.pathname !== "/") {
      navigate(`/${id}`);
      return;
    }
    lenis?.scrollTo(id, { offset: -84, duration: 1.5 });
  };

  const toTop = () => lenis?.scrollTo(0, { duration: 1.6 });

  return (
    <footer className="relative overflow-hidden border-t border-line bg-panel">
      <div className="mx-auto max-w-7xl px-5 pt-16 sm:px-8 lg:pt-24">
        <div className="grid grid-cols-1 gap-10 pb-16 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <LogoMark size={40} />
              <div className="leading-none">
                <p className="font-serif text-2xl tracking-tight text-bone">
                  Nex<span className="text-terracotta">.</span>
                </p>
                <p className="mt-1 font-accent text-[9px] uppercase tracking-[0.38em] text-stone">
                  Studio Design
                </p>
              </div>
            </div>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-stone">
              La vetrina digitale ad alte conversioni per ristoranti, hotel, B&B e botteghe
              artigiane in Italia.
            </p>
            <div className="mt-6 flex gap-3">
              {[
                { icon: Instagram, id: "instagram", label: "Instagram" },
                { icon: Linkedin, id: "linkedin", label: "LinkedIn" },
                { icon: Facebook, id: "facebook", label: "Facebook" },
              ].map((s) => (
                <a
                  key={s.id}
                  href="#top"
                  onClick={go("#top")}
                  aria-label={s.label}
                  data-testid={`footer-social-${s.id}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-stone transition-colors duration-300 hover:border-terracotta hover:text-terracotta"
                >
                  <s.icon size={16} />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="font-accent text-[11px] uppercase tracking-[0.28em] text-terracotta">
              Esplora
            </p>
            <ul className="mt-5 space-y-3">
              {NAV.map((l) => (
                <li key={l.id}>
                  <a
                    href={l.id}
                    onClick={go(l.id)}
                    data-testid={`footer-link-${l.id.replace(/[#/]/, "")}`}
                    className="text-sm text-stone transition-colors duration-300 hover:text-bone"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
            <a
              href="/strumenti"
              onClick={go("/strumenti")}
              data-testid="footer-link-strumenti"
              className="mt-7 inline-flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.28em] text-faded transition-colors duration-300 hover:text-terracotta"
            >
              <Lock size={11} /> Area di lavoro
            </a>
          </div>

          <div className="md:col-span-4">
            <p className="font-accent text-[11px] uppercase tracking-[0.28em] text-terracotta">
              Contatti
            </p>
            <ul className="mt-5 space-y-3 text-sm text-stone">
              <li>
                <a href="mailto:info@nexstudiodesign.it" data-testid="footer-email-info" className="transition-colors duration-300 hover:text-bone">
                  info@nexstudiodesign.it
                </a>
                <span className="ml-2 font-accent text-[9px] uppercase tracking-[0.2em] text-faded">Preventivi</span>
              </li>
              <li>
                <a href="mailto:assistenza@nexstudiodesign.it" data-testid="footer-email-assistenza" className="transition-colors duration-300 hover:text-bone">
                  assistenza@nexstudiodesign.it
                </a>
                <span className="ml-2 font-accent text-[9px] uppercase tracking-[0.2em] text-faded">Clienti</span>
              </li>
              <li><a href="tel:+393522337690" data-testid="footer-phone" className="transition-colors duration-300 hover:text-bone">+39 352 233 7690</a></li>
              <li>Venezia · Mestre · Veneto</li>
              <li className="pt-2 font-accent text-[10px] uppercase tracking-[0.22em] text-faded">
                Lun — Ven · 9:00 — 18:00
              </li>
            </ul>
          </div>
        </div>

        <Reveal y={40}>
          <div className="select-none overflow-hidden border-t border-line pt-8" aria-hidden="true">
            <p className="whitespace-nowrap font-serif text-[clamp(2.75rem,9.4vw,8.25rem)] leading-[0.86] tracking-tight text-bone">
              NEX<span className="text-terracotta">.</span>
            </p>
            <p className="text-outline whitespace-nowrap font-serif text-[clamp(2.75rem,9.4vw,8.25rem)] leading-[0.86] tracking-tight">
              STUDIO DESIGN
            </p>
          </div>
        </Reveal>

        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-line py-7">
          <p className="text-xs text-faded">
            © {new Date().getFullYear()} Nex Studio Design · Venezia · Privacy Policy · Cookie Policy
          </p>
          <button
            onClick={toTop}
            data-testid="footer-back-to-top"
            aria-label="Torna su"
            className="group flex h-11 w-11 items-center justify-center rounded-full border border-line text-stone transition-colors duration-300 hover:border-terracotta hover:text-terracotta"
          >
            <ArrowUp size={17} className="transition-transform duration-300 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </footer>
  );
}